/**
 * THE OWED-REACTION LIST behind `useReactionQueue` - who owes a reaction,
 * how loudly each client is told, and which one the prompt answers.
 *
 * Pure, and zero React dependency: the hook memoizes these and owns the
 * focus request. See `hooks/useReactionQueue.ts` for why every client sees
 * every reaction, and `engine/reactionAlarm.ts` for the loudness ruling.
 */
import type { ReactionAlarmPolicy } from './reactionAlarm';

/** How loudly one client should present one owed reaction. */
type ReactionVolume = 'loud' | 'calm';

export interface OwedReaction {
  /** The combatant who owes it - also the defence document's id. */
  combatantId: string;
  name: string;
  doc: Record<string, unknown>;
  volume: ReactionVolume;
  /** Milliseconds left before it expires, or null when it does not. */
  remainingMs: number | null;
}

/** Who this client is, for the volume. */
interface Listener {
  actorId: string | null;
  isGm: boolean;
  alarmPolicy: ReactionAlarmPolicy;
  isControlledByActor?: (combatantId: string) => boolean;
}

/**
 * Loud only when this client has some claim on the combatant.
 *
 * Acting as someone IS the informal claim - it is what a player does when
 * they pick up a character - so it needs no new data and nothing synced.
 */
function volumeFor(combatantId: string, { actorId, isGm, alarmPolicy, isControlledByActor }: Listener): ReactionVolume {
  if (actorId && combatantId === actorId) return 'loud';
  // A CONTROLLER HEARS THEIR NETWORK. The sergeant's player is woken when a
  // servitor is shot at, because in practice that servitor is theirs to
  // answer for - without the app claiming anybody OWNS anybody.
  if (isControlledByActor?.(combatantId)) return 'loud';
  if (isGm && alarmPolicy === 'actorsAndGm') return 'loud';
  return 'calm';
}

/** Everything `listOwed` reads. */
export interface OwedInput {
  pendingDefences: Map<string, Record<string, unknown>> | null | undefined;
  pendingAttack: { tabId?: string } | null;
  currentTab: { target?: unknown } | null | undefined;
  roster: { id: string; name: string }[];
  listener: Listener;
  timeoutMs?: number;
  /** The clock, as of now. */
  clock: number;
}

/** Milliseconds a document has left, or null without a creation time or a timeout. */
function remainingOf(doc: Record<string, unknown>, timeoutMs: number | undefined, clock: number): number | null {
  const createdAt = typeof doc?.createdAt === 'number' ? doc.createdAt : null;
  return createdAt && timeoutMs ? Math.max(0, createdAt + timeoutMs - clock) : null;
}

/**
 * Every outstanding reaction. In a session these arrive from the backend; in
 * solo the same map is kept locally, which is what lets one blast owe
 * several reactions with nobody else at the table (v8.154.0).
 *
 * A reaction for somebody this client has not synced yet is HELD, not shown:
 * there is no name to put on it. The sync layer keeps the entry, and the
 * hook's memo depends on the roster, so it appears by itself the moment they
 * arrive (v8.160.0).
 */
function sharedOwed(input: OwedInput, nameOf: (id: string) => string): OwedReaction[] {
  if (!input.pendingDefences) return [];
  const known = new Set(input.roster.map((c) => c.id));
  return [...input.pendingDefences]
    .filter(([combatantId]) => known.has(combatantId))
    .map(([combatantId, doc]) => ({
      combatantId,
      name: nameOf(combatantId),
      doc,
      volume: volumeFor(combatantId, input.listener),
      remainingMs: remainingOf(doc, input.timeoutMs, input.clock),
    }));
}

/** The attacker's own, before it has been shared - solo play, and the beat before the document lands. */
function localOwed(input: OwedInput, nameOf: (id: string) => string, shared: OwedReaction[]): OwedReaction[] {
  const localId = input.pendingAttack?.tabId;
  if (!localId || shared.some((o) => o.combatantId === localId)) return [];
  const doc = { ...input.pendingAttack, frozenTarget: input.currentTab?.target, attackerName: null };
  return [
    { combatantId: localId, name: nameOf(localId), doc, volume: volumeFor(localId, input.listener), remainingMs: null },
  ];
}

/** Everything owed right now, loudest first - so the one that wants answering is on top. */
export function listOwed(input: OwedInput): OwedReaction[] {
  const nameOf = (id: string) => input.roster.find((c) => c.id === id)?.name ?? 'Unknown';
  const shared = sharedOwed(input, nameOf);
  const out = [...shared, ...localOwed(input, nameOf, shared)];
  return out.sort((a, b) => (a.volume === b.volume ? 0 : a.volume === 'loud' ? -1 : 1));
}

/**
 * The one the prompt answers: a live chain's wins over everything (it is
 * modal - answering another would overwrite its steps), then the one asked
 * for, then the first owed.
 */
export function pickFocused(
  owed: OwedReaction[],
  chainTabId: string | null | undefined,
  focusRequest: string | null,
): OwedReaction | null {
  if (!owed.length) return null;
  const find = (id: string | null | undefined) => (id ? owed.find((o) => o.combatantId === id) : undefined);
  return find(chainTabId) ?? find(focusRequest) ?? owed[0];
}
